import { useState } from "react";
import { Search, Plus, Loader2, Trash2, ExternalLink } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useInvestigations, useCreateInvestigation, useDeleteInvestigation } from "@/api/queries";
import { toast } from "sonner";

const STATUS_VARIANTS: Record<string, string> = {
  completed: "bg-emerald-500/10 text-emerald-500",
  running: "bg-blue-500/10 text-blue-500",
  failed: "bg-red-500/10 text-red-500",
  draft: "bg-zinc-500/10 text-zinc-400",
  queued: "bg-amber-500/10 text-amber-500",
};

const STATUSES = [
  { value: "all", label: "Все" },
  { value: "draft", label: "Черновик" },
  { value: "queued", label: "В очереди" },
  { value: "running", label: "Выполняется" },
  { value: "completed", label: "Завершён" },
  { value: "failed", label: "Ошибка" },
];

const TARGET_TYPES = [
  { value: "inn", label: "ИНН" },
  { value: "email", label: "Email" },
  { value: "domain", label: "Домен" },
  { value: "phone", label: "Телефон" },
  { value: "username", label: "Username" },
  { value: "telegram", label: "Telegram" },
  { value: "vk", label: "VK" },
  { value: "custom", label: "Другое" },
];

const PRIORITIES = [
  { value: "low", label: "Низкий" },
  { value: "normal", label: "Обычный" },
  { value: "high", label: "Высокий" },
  { value: "urgent", label: "Срочный" },
];

const STATUS_LABELS: Record<string, string> = {
  draft: "Черновик",
  queued: "В очереди",
  running: "Выполняется",
  completed: "Завершён",
  failed: "Ошибка",
};

export function Investigations() {
  const [filter, setFilter] = useState<string>("all");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: "",
    target: "",
    target_type: "custom",
    description: "",
    priority: "normal",
  });

  const { data, isLoading, error } = useInvestigations({
    size: 50,
    status: filter !== "all" ? filter : undefined,
  });
  const create = useCreateInvestigation();
  const del = useDeleteInvestigation();

  const onCreate = async () => {
    if (!form.title.trim() || !form.target.trim()) {
      toast.error("Заполните название и цель");
      return;
    }
    try {
      await create.mutateAsync({
        title: form.title,
        target: form.target,
        target_type: form.target_type,
        description: form.description || undefined,
        priority: form.priority,
      });
      toast.success("Сбор данных создан");
      setCreating(false);
      setForm({ title: "", target: "", target_type: "custom", description: "", priority: "normal" });
    } catch {
      // toast
    }
  };

  const onDelete = async (id: string, title: string) => {
    if (!confirm(`Удалить «${title}»? Это необратимо.`)) return;
    try {
      await del.mutateAsync(id);
      toast.success("Удалено");
    } catch {
      // toast
    }
  };

  const items = (data?.items ?? []) as any[];
  const total = data?.total ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Сбор аналитических данных
          </h1>
          <p className="text-muted-foreground mt-1">
            Всего сбора данных: {total}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>

          <Button onClick={() => setCreating(!creating)}>
            <Plus className="h-4 w-4" />
            {creating ? "Отмена" : "Новый сбор данных"}
          </Button>
        </div>
      </div>

      {creating && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Новый сбор данных</CardTitle>
            <CardDescription>Заполните поля и нажмите «Создать»</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-sm font-medium">Название</label>
              <Input
                placeholder="Например: Контрагент ООО «Альфа»"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <label className="text-sm font-medium">Цель (ИНН, email, домен, телефон)</label>
                <Input
                  placeholder="7707083893 или info@company.ru"
                  value={form.target}
                  onChange={(e) => setForm({ ...form, target: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Тип цели</label>
                <select
                  value={form.target_type}
                  onChange={(e) => setForm({ ...form, target_type: e.target.value })}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  {TARGET_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Приоритет</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {PRIORITIES.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Описание (опционально)</label>
              <Input
                placeholder="Контекст сбора данных"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setCreating(false)}>Отмена</Button>
              <Button onClick={onCreate} disabled={create.isPending}>
                {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Создать
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="border-red-500/50">
          <CardContent className="p-6 text-red-500">
            Ошибка загрузки: {(error as any)?.response?.data?.error?.message || String(error)}
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-4 w-4" /> Нет сборов данных
            </CardTitle>
            <CardDescription>
              Создайте первый — кнопка «Новый сбор данных» вверху справа.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="grid gap-3">
          {items.map((inv: any) => (
            <Card key={inv.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-lg truncate">{inv.title}</CardTitle>
                    <CardDescription className="mt-1 truncate">
                      Цель: <span className="font-mono text-foreground">{inv.target}</span> ·{" "}
                      <span className="uppercase">{inv.target_type}</span>
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline">{inv.priority}</Badge>
                    <Badge
                      variant="outline"
                      className={STATUS_VARIANTS[inv.status] || "bg-zinc-500/10 text-zinc-400"}
                    >
                      {STATUS_LABELS[inv.status] || inv.status}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between text-sm flex-wrap gap-2">
                  <div className="flex items-center gap-4 text-muted-foreground flex-wrap">
                    <span>находок: {inv.findings_count}</span>
                    {inv.risk_score != null && (
                      <span>
                        Риск:{" "}
                        <span
                          className={
                            inv.risk_score >= 70
                              ? "text-red-500 font-medium"
                              : inv.risk_score >= 40
                              ? "text-amber-500 font-medium"
                              : "text-emerald-500 font-medium"
                          }
                        >
                          {inv.risk_score}
                        </span>
                      </span>
                    )}
                    <span>{new Date(inv.created_at).toLocaleString("ru-RU")}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm">
                      <ExternalLink className="h-3 w-3" /> Открыть
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(inv.id, inv.title)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                {inv.tags?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {inv.tags.map((t: string) => (
                      <Badge key={t} variant="secondary" className="text-xs">
                        {t}
                      </Badge>
                    ))}
                  </div>
                )}
                {inv.summary && (
                  <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{inv.summary}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
